import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../../app/store'
import {
    fetchCustomers,
    fetchCustomer,
    fetchCustomerShipTos,
    fetchContacts,
    cancelSearch,
    UpdateIdentifierPayload,
    fetchUpdateIdentifier,
    AddShipToPayload,
    fetchAddShipTo,
    fetchAddContact,
    fetchRequirements,
    fetchPricingTiers,
    AddCustomerPayload,
    fetchAddCustomer,
    fetchSaveContact,
    PostSaveContactPayload,
    fetchSaveCustomer,
    SaveCustomerPayload,
} from './customerAPI'
import { quitQuote } from '../order/orderSlice'
import { PACustomerContactViewRow as oldPARow } from '../../@types/PricingAvailabilityDS'
import { CUSTOMER_REASONS, CUSTOMER_TYPES } from '../../consts'
import { MODALS, setModal } from '../meta/metaSlice'
import { fqAlert } from '../../GlobalComponents/NotificationManager/NotificationManager'
import { forceReleaseOrderLock } from '../lock/lockSlice'
type AccountCode = string

export type DateToString<T> = {
    [k in keyof T]: T[k] extends Date | undefined
        ? string
        : T[k] extends object
        ? DateToString<T[k]>
        : T[k]
}
// type Row = DateToString<{ val: { test: Date } }>
// type Row2 = DateToString<{ val: Array<{ test: Date }> }>
// let xx: any = {}
// let yy: Row2 = xx
// yy.val[0].test
interface OrderNumberExactMatchReturn {
    ContactEmail: string
    ContactKey: number
    CustomerAccountCode: string
    OrderNumber: string
}
export interface PACustomerContactViewRow extends DateToString<oldPARow> {
    ExactMatch: boolean
    CustomerTypeName?: string
    OrderNumberExactMatch?: OrderNumberExactMatchReturn
}
export interface AddContactPayload {
    firstName: string
    lastName: string
    title: string
    email: string
    phone: string
    accountCode: string
}
export type PACustomerContactView = Array<PACustomerContactViewRow>
let x: any = {}
let y: PACustomerContactViewRow = x
y.LastTransaction = 'a'
export interface CustomerState {
    currentCustomer?: PACustomerContactViewRow
    customerShipTo: Record<AccountCode, CustomerShipToView>
    customerContacts: Record<AccountCode, ContactCustomerAccountCodeView>
    customerRequirements: Record<AccountCode, CustomerRequirements>
    currentModal: MODALS
    CustomerAccounts: PACustomerContactView
    loadingInfo: Record<AccountCode, Partial<PACustomerContactViewRow>>
    currentFilter: string
    currentSearch: string
    startedCustomerSearch: boolean
    customersLoading: LOADING_STATES
    contactsLoading: LOADING_STATES
    shipTosLoading: LOADING_STATES
    pricingTiers: CustomerContractHeaderView
}
export enum LOADING_STATES {
    INIT = 'INIT', // nothing tried
    LOADING = 'LOADING',
    DONE = 'DONE',
    FAILED = 'FAILED',
}

const initialState: CustomerState = {
    currentModal: MODALS.NONE,
    CustomerAccounts: [],
    customerShipTo: {},
    customerContacts: {},
    customerRequirements: {},
    currentFilter: '',
    currentSearch: '',
    startedCustomerSearch: false,
    customersLoading: LOADING_STATES.INIT,
    contactsLoading: LOADING_STATES.INIT,
    pricingTiers: [],
    shipTosLoading: LOADING_STATES.INIT,
    loadingInfo: {},
}

export const reasonNameFromId = (id: number) => {
    return Object.values(CUSTOMER_REASONS).find((row) => row.reasonId === id)?.reasonName
}
const filterAccounts = (query: string) => (row: PACustomerContactViewRow) => {
    let searchFields: Partial<PACustomerContactViewRow> = { ...row }
    // dont search these fields
    delete searchFields.ExactMatch
    delete searchFields.ARBalance
    delete searchFields.TaxExempt

    let searchFieldArray = Object.keys(searchFields) as Array<keyof PACustomerContactViewRow>
    let searchValues = searchFieldArray.map((key) => row[key])

    // if it's a date do some date searching
    let possibleDate = new Date(query)
    if (/\d{1,2}\/\d{1,2}/.test(query)) {
        let dateParts = query.split('/')
        possibleDate = new Date(1900, parseInt(dateParts[0]) - 1, parseInt(dateParts[1]))
    }
    if (!isNaN(possibleDate.getTime())) {
        return (
            searchValues.join(' ').toLowerCase().includes(query) ||
            searchValues.some((rowRaw) => {
                if (!(typeof rowRaw === 'string')) return false
                let row = new Date(rowRaw)
                if (isNaN(row.getTime())) return false
                return (
                    Math.abs(
                        Math.floor((row.getTime() - possibleDate.getTime()) / (1000 * 60 * 60 * 24))
                    ) < 1 ||
                    (possibleDate.getFullYear() === 1900 &&
                        row.getMonth() === possibleDate.getMonth() &&
                        row.getDate() === possibleDate.getDate())
                )
            })
        )
    }

    return searchValues.join(' ').toLowerCase().includes(query)
}

export const isEmail = (value: string) => {
    return /^.+?@.+?\..+$/.test(value)
}
export const getCustomers = createAsyncThunk(
    'customer/getCustomers',
    async (searchTerm: string, thunkAPI) => {
        thunkAPI.dispatch(setCurrentSearch(searchTerm))
        if (searchTerm.length < 2) return []
        let effectiveSearchTerm = searchTerm.substr(0, 200)

        const data = await fetchCustomers(effectiveSearchTerm)
        return data
    }
)
export const validUpdateIdentifierPayload = (payload: UpdateIdentifierPayload) => {
    if (!payload.accountCode || payload.accountCode.trim().length === 0) return false
    if (!payload.reasonId) return false
    if (payload.reasonId === 8 && (!payload.qualifier || payload.qualifier.trim().length === 0))
        return false
    return true
}
export const postUpdateIdentifier = createAsyncThunk(
    'customer/postUpdateIdentifier',
    async (payload: UpdateIdentifierPayload) => {
        await fetchUpdateIdentifier(payload)
    }
)

export const postSaveCustomer = createAsyncThunk(
    'customer/postSaveCustomer',
    async (payload: SaveCustomerPayload) => {
        await fetchSaveCustomer(payload)
    }
)

export const validShipToPayload = (payload: AddShipToPayload) => {
    if (!payload.accountCode || payload.accountCode.trim().length === 0) return false
    // only require state if country is US
    if (
        (!payload.state || !payload.state.trim()) &&
        payload.country &&
        payload.country.trim() === 'US'
    )
        return false
    // all fields required
    return !Object.entries(payload).some(
        (keyValue) =>
            keyValue[0] !== 'address2' &&
            keyValue[0] !== 'isDefault' &&
            keyValue[0] !== 'state' &&
            keyValue[1].trim().length === 0
    )
}
export const postAddShipTo = createAsyncThunk(
    'customer/postAddShipTo',
    async (payload: AddShipToPayload) => {
        await fetchAddShipTo(payload)
    }
)

export const validAddContactPayload = (payload: AddContactPayload) => {
    return Object.entries(payload).every(
        (keyValue) =>
            keyValue[0] === 'title' || keyValue[0] === 'phone' || keyValue[1].trim().length !== 0
    )
}
export const postAddContact = createAsyncThunk(
    'customer/postAddContact',
    async (payload: AddContactPayload) => {
        let contactKey = await fetchAddContact(payload)
        return contactKey
    }
)

export const postSaveContact = createAsyncThunk(
    'customer/postSaveContact',
    async (payload: PostSaveContactPayload) => {
        let contactKey = await fetchSaveContact(payload)
        return contactKey
    }
)

export const validAddCustomerPayload = (payload: AddCustomerPayload) => {
    if (!payload.Customer || payload.Customer.length !== 1) return false
    let customer = payload.Customer[0]
    return (
        customer.AccountCode && customer.ContactEmail && customer.ContactName && customer.BillToName
    )
}
export const postAddCustomer = createAsyncThunk(
    'customer/postAddCustomer',
    async (payload: AddCustomerPayload, thunkapi) => {
        // throw Error("Test error")
        await fetchAddCustomer(payload)
        return 1
    }
)

export const getCustomer = createAsyncThunk('customer/getCustomer', async (id: string) => {
    const response = await fetchCustomer(id)
    return response
})

export const getContacts = createAsyncThunk('customer/getContacts', async (id: string) => {
    const response = await fetchContacts(id)
    return { data: response, accountCode: id }
})

export const getRequirements = createAsyncThunk('customer/getRequirements', async (id: string) => {
    const response = await fetchRequirements(id)
    return { data: response, accountCode: id }
})

export const getPricingTiers = createAsyncThunk('customer/getPricingTiers', async () => {
    const response = await fetchPricingTiers()
    return { data: response }
})

export const getCustomerShipTos = createAsyncThunk(
    'customer/getCustomerShipTos',
    async (id: string) => {
        const response = await fetchCustomerShipTos(id)
        // The value we return becomes the `fulfilled` action payload
        return response
    }
)
export const openShipToModify = createAsyncThunk(
    'customer/openShipToModify',
    async (customer: PACustomerContactViewRow, thunkAPI) => {
        thunkAPI.dispatch(customerSlice.actions.selectCustomer(customer))
        thunkAPI.dispatch(setModal(MODALS.MODIFY))
        thunkAPI.dispatch(getCustomerShipTos(customer.AccountCode))
    }
)

export const openRecipient = createAsyncThunk(
    'customer/openRecipient',
    async (customer: PACustomerContactViewRow, thunkAPI) => {
        thunkAPI.dispatch(customerSlice.actions.selectCustomer(customer))
        thunkAPI.dispatch(setModal(MODALS.RECIPIENT))
        thunkAPI.dispatch(getContacts(customer.AccountCode))
    }
)

export const openIdentifierUpdate = createAsyncThunk(
    'customer/openIdentifierUpdate',
    async (customer: PACustomerContactViewRow, thunkAPI) => {
        thunkAPI.dispatch(customerSlice.actions.selectCustomer(customer))
        thunkAPI.dispatch(setModal(MODALS.UPDATE))
    }
)

export const openCustomerTypeModal = createAsyncThunk(
    'customer/openCustomerTypeModal',
    async (customer: PACustomerContactViewRow, thunkAPI) => {
        thunkAPI.dispatch(customerSlice.actions.selectCustomer(customer))
        thunkAPI.dispatch(setModal(MODALS.CUSTOMER_TYPE))
    }
)

export const openUniqueCustomerNameModal = createAsyncThunk(
    'customer/openUniqueCustomerNameModal',
    async (customer: PACustomerContactViewRow, thunkAPI) => {
        thunkAPI.dispatch(customerSlice.actions.selectCustomer(customer))
        thunkAPI.dispatch(setModal(MODALS.UNIQUE_CUSTOMER_NAME))
    }
)

// export const openShipToModify = (customer: PACustomerContactViewRow) => {
//     return (dispatch: AppDispatch) => {
//         dispatch(customerSlice.actions.selectCustomer(customer))
//         dispatch(setModal(MODALS.MODIFY))
//         dispatch(getCustomerShipTos(customer.AccountCode))
//     }
// }

// export const openRecipient = (customer: PACustomerContactViewRow) => {
//     return (dispatch: AppDispatch) => {
//         dispatch(customerSlice.actions.selectCustomer(customer))
//         dispatch(setModal(MODALS.RECIPIENT))
//         dispatch(getContacts(customer.AccountCode))
//     }
// }

// export const openIdentifierUpdate = (customer: PACustomerContactViewRow) => {
//     return (dispatch: AppDispatch) => {
//         dispatch(customerSlice.actions.selectCustomer(customer))
//         dispatch(setModal(MODALS.UPDATE))
//     }
// }
export const customerSlice = createSlice({
    name: 'customer',
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        selectCustomer: (state, action: PayloadAction<PACustomerContactViewRow>) => {
            let customer = action.payload
            // state.currentCustomer = state.customerList.find(row => row.AccountCode === accountCode)
            state.currentCustomer = customer
        },
        setAccountFilter: (state, action: PayloadAction<string>) => {
            state.currentFilter = action.payload
        },
        setStartedCustomerSearch: (state, action: PayloadAction<boolean>) => {
            state.startedCustomerSearch = action.payload
        },
        setCurrentSearch: (state, action: PayloadAction<string>) => {
            state.currentSearch = action.payload
            if (action.payload === '') cancelSearch()
        },
        setCustomersLoading: (state, action: PayloadAction<LOADING_STATES>) => {
            state.customersLoading = action.payload
        },
        setCustomerName: (state, action: PayloadAction<[string, PACustomerContactViewRow]>) => {
            let name = action.payload[0]
            let cust = action.payload[1]
            if (state.CustomerAccounts.length > 0) {
                state.CustomerAccounts.map((customer: PACustomerContactViewRow) => {
                    if (JSON.stringify(customer) === JSON.stringify(cust)) {
                        customer.CustomerName = name
                    }
                })
                const tempCust = state.currentCustomer
                if (tempCust) {
                    tempCust.CustomerName = name
                    state.currentCustomer = tempCust
                }
            } else {
                state.currentCustomer = { ...cust, CustomerName: name }
            }
        },
        setCustomerTypeName: (state, action: PayloadAction<string>) => {
            state.currentCustomer!.CustomerTypeName = action.payload
        },
        resetCustomerSearchFields: (state) => {
            state.currentCustomer = undefined
            state.CustomerAccounts = []
            state.customersLoading = LOADING_STATES.INIT
            state.currentSearch = ''
            state.currentFilter = ''
            state.startedCustomerSearch = false
        },
        openRecipientModal: (state, action: PayloadAction<PACustomerContactViewRow>) => {
            let customer = action.payload
            state.currentCustomer = customer
            state.currentModal = MODALS.RECIPIENT
        },
        addShipTo: (state, action: PayloadAction<any>) => {
            let { accountCode, ...fields } = action.payload
            let newShipTo: CustomerShipToViewRow = {
                AccountCode: accountCode,
                ShipToID: -1,
                AvailabilityMode: 'a',
                QuoteAttachmentFormats: 'a',
                ShipToName: state.currentCustomer?.ShipToName,
                ShipToAddress1: fields.address,
                ShipToAddress2: fields.suite,
                ShipToCity: fields.city,
                ShipToState: fields.state,
                ShipToCountry: fields.country,
                ShipToZipcode: fields.zipcode,
            }
            state.customerShipTo[accountCode].push(newShipTo)
        },
        addContact: (state, action: PayloadAction<AddContactPayload>) => {
            // let { firstName, email, accountCode } = action.payload
            // let newContact : ContactCustomerAccountCodeViewRow = {
            //   FirstName: name.split(/\s+/)[0],
            //   LastName: name.split(/\s+/)[1],
            //   EmailAddress: email,
            // }
            // state.customerContacts[accountCode].push(newContact)
        },
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(getCustomers.pending, (state) => {
                state.customersLoading = LOADING_STATES.LOADING
            })
            .addCase(getCustomers.fulfilled, (state, action) => {
                state.customersLoading = LOADING_STATES.DONE
                state.CustomerAccounts = action.payload
            })
            .addCase(getCustomers.rejected, (state, action) => {
                if (action.error.name === 'AbortError') {
                    state.customersLoading = LOADING_STATES.DONE
                } else {
                    state.customersLoading = LOADING_STATES.FAILED
                }
            })

        builder
            .addCase(getCustomerShipTos.pending, (state) => {
                state.shipTosLoading = LOADING_STATES.LOADING
                // state.status = 'loading';
            })
            .addCase(getCustomerShipTos.fulfilled, (state, action) => {
                // state.status = 'idle';
                if (action.payload[0])
                    state.customerShipTo[action.payload[0].AccountCode.trim()] = action.payload
                state.shipTosLoading = LOADING_STATES.DONE
            })
        builder
            .addCase(getContacts.pending, (state) => {
                state.contactsLoading = LOADING_STATES.LOADING
            })
            .addCase(getContacts.fulfilled, (state, action) => {
                let { accountCode, data } = action.payload
                state.contactsLoading = LOADING_STATES.DONE

                state.customerContacts[accountCode] = data
            })
            .addCase(getContacts.rejected, (state) => {
                state.contactsLoading = LOADING_STATES.FAILED
            })

        builder
            .addCase(getRequirements.pending, (state) => {})
            .addCase(getRequirements.fulfilled, (state, action) => {
                let { accountCode, data } = action.payload

                state.customerRequirements[accountCode] = data
            })
            .addCase(getRequirements.rejected, (state) => {})
        builder
            .addCase(getPricingTiers.pending, (state) => {})
            .addCase(getPricingTiers.fulfilled, (state, action) => {
                let { data } = action.payload

                state.pricingTiers = data
            })
            .addCase(getPricingTiers.rejected, (state) => {})
        builder
            .addCase(postUpdateIdentifier.pending, (state, action) => {
                let { arg } = action.meta
                let currentLoading = state.loadingInfo[arg.accountCode] || {}
                currentLoading.ReasonName = reasonNameFromId(arg.reasonId)
                currentLoading.CustomerReasonQualifier = arg.qualifier
                state.loadingInfo[arg.accountCode] = currentLoading
            })
            .addCase(postUpdateIdentifier.fulfilled, (state, action) => {
                let { arg } = action.meta
                if (state.CustomerAccounts.length > 0) {
                    let customer = state.CustomerAccounts.find(
                        (row) => row.AccountCode === arg.accountCode
                    )!
                    let currentCustomer = state.currentCustomer
                    customer.ReasonName = reasonNameFromId(arg.reasonId)
                    customer.CustomerReasonQualifier = arg.qualifier
                    if (currentCustomer) {
                        currentCustomer.ReasonName = reasonNameFromId(arg.reasonId)
                        currentCustomer.CustomerReasonQualifier = arg.qualifier
                    }
                } else {
                    if (state.currentCustomer) {
                        state.currentCustomer.ReasonName = reasonNameFromId(arg.reasonId)
                        state.currentCustomer.CustomerReasonQualifier = arg.qualifier
                    }
                }

                let currentLoading = state.loadingInfo[arg.accountCode]
                if (currentLoading) {
                    delete currentLoading.ReasonName
                    delete currentLoading.CustomerReasonQualifier
                }
            })
            .addCase(postUpdateIdentifier.rejected, (state, action) => {
                let { arg } = action.meta
                let currentLoading = state.loadingInfo[arg.accountCode]
                if (currentLoading) {
                    delete currentLoading.ReasonName
                    delete currentLoading.CustomerReasonQualifier
                }
            })

        builder
            .addCase(postSaveCustomer.pending, (state, action) => {
                let { arg } = action.meta
                let currentLoading = state.loadingInfo[arg.accountCode] || {}
                currentLoading.CustomerTypeName = Object.values(CUSTOMER_TYPES).find(
                    (row) => row.typeId === arg.customerType
                )?.typeText
                state.loadingInfo[arg.accountCode] = currentLoading
            })
            .addCase(postSaveCustomer.fulfilled, (state, action) => {
                let { arg } = action.meta
                if (state.CustomerAccounts.length > 0) {
                    let customer = state.CustomerAccounts.find(
                        (row) => row.AccountCode === arg.accountCode
                    )!
                    let currentCustomer = state.currentCustomer

                    customer.CustomerTypeName = Object.values(CUSTOMER_TYPES).find(
                        (row) => row.typeId === arg.customerType
                    )?.typeText
                    if (currentCustomer) {
                        currentCustomer.CustomerTypeName = Object.values(CUSTOMER_TYPES).find(
                            (row) => row.typeId === arg.customerType
                        )?.typeText
                    }
                } else {
                    // Coming in from creating presale account
                    if (state.currentCustomer) {
                        state.currentCustomer.CustomerTypeName = Object.values(CUSTOMER_TYPES).find(
                            (row) => row.typeId === arg.customerType
                        )?.typeText
                    }
                }

                let currentLoading = state.loadingInfo[arg.accountCode]
                if (currentLoading) {
                    delete currentLoading.CustomerTypeName
                }
            })
            .addCase(postSaveCustomer.rejected, (state, action) => {
                let { arg } = action.meta
                let currentLoading = state.loadingInfo[arg.accountCode]
                if (currentLoading) {
                    delete currentLoading.CustomerTypeName
                }
            })

        builder
            .addCase(postAddShipTo.pending, (state, action) => {})
            .addCase(postAddShipTo.fulfilled, (state, action) => {
                let { arg } = action.meta
                if (arg.isDefault) {
                    let customer = state.CustomerAccounts.find(
                        (row) => row.AccountCode === arg.accountCode
                    )
                    if (customer) {
                        customer!.ShipToAddress1 = arg.address1
                        customer!.ShipToAddress2 = arg.address2
                        customer!.ShipToZipcode = arg.zipcode
                        customer!.ShipToState = arg.state
                        customer!.ShipToCity = arg.city
                        customer!.ShipToName = arg.name
                    }
                }
            })
            .addCase(postAddShipTo.rejected, (state, action) => {
                fqAlert({ type: 'error', title: 'Failed to add ship to' })
            })

        builder
            .addCase(postAddContact.pending, (state, action) => {})
            .addCase(postAddContact.fulfilled, (state, action) => {
                let { arg } = action.meta
                let { accountCode, firstName, lastName, email } = arg
                let contactKey = action.payload
                let customerContacts = state.customerContacts[accountCode]
                let newRow: ContactCustomerAccountCodeViewRow = {
                    CustomerAccountCode: accountCode,
                    ContactKey: contactKey,
                    FirstName: firstName,
                    LastName: lastName,
                    EmailAddress: email,
                    // TODO manually pull out unused fields in types.
                    //      Currently optional is based on if null is allowed in db.
                    //unused but required
                    ContactCompanyName: '',
                    Prospect: true,
                }
                if (customerContacts) customerContacts.push(newRow)
                else {
                    state.customerContacts[accountCode] = [newRow]
                }
            })
            .addCase(postAddContact.rejected, (state, action) => {
                fqAlert({ type: 'error', title: 'Failed to add contact' })
            })
        builder.addCase(quitQuote.fulfilled, (state) => {
            state.currentFilter = ''
            state.CustomerAccounts = []
            state.currentSearch = ''
            state.customersLoading = LOADING_STATES.INIT
        })
        builder.addCase(forceReleaseOrderLock.fulfilled, (state, action) => {
            state.currentFilter = ''
            state.CustomerAccounts = []
            state.currentSearch = ''
            state.customersLoading = LOADING_STATES.INIT
        })
    },
})

export const {
    addContact,
    selectCustomer,
    setAccountFilter,
    setCurrentSearch,
    setStartedCustomerSearch,
    setCustomerName,
    setCustomerTypeName,
    setCustomersLoading,
    resetCustomerSearchFields,
    addShipTo,
} = customerSlice.actions

export const selectFilteredAccounts2 = (state: RootState) =>
    state.customer.CustomerAccounts.filter(
        filterAccounts(state.customer.currentFilter.toLowerCase())
    )
export const selectFilteredAccounts = createSelector(
    (state: RootState) => state.customer.CustomerAccounts,
    (state: RootState) => state.customer.currentFilter,
    (accounts, currentFilter) => accounts.filter(filterAccounts(currentFilter.toLowerCase()))
)
export const selectFilteredContacts = createSelector(
    (state: RootState, accountCode: string) => state.customer.customerContacts[accountCode] || [],
    (_1: any, _2: any, filter: string) => filter,
    (contacts, filter) => {
        let contactsWithEmails = contacts.filter(
            (row) =>
                row.EmailAddress!.trim().length !== 0 &&
                JSON.stringify(row).toLowerCase().includes(filter.toLowerCase())
        )
        contactsWithEmails.sort((a, b) =>
            a.FirstName.toLowerCase() > b.FirstName.toLowerCase() ? 1 : -1
        )

        return contactsWithEmails
    }
)
export const selectOrderedShipTos = createSelector(
    (state: RootState, accountCode: string) => {
        return state.customer.customerShipTo[accountCode.trim()] || []
    },
    (state: RootState) => state.order.currentCustomer,
    (shipTos, customer) => {
        if (!customer) return []
        let res = [...shipTos]
        let defaultShipToIndex = shipTos.findIndex((row) => {
            return (
                row.ShipToAddress1 === customer?.ShipToAddress1 &&
                row.ShipToAddress2 === customer?.ShipToAddress2 &&
                row.ShipToZipcode === customer?.ShipToZipcode &&
                row.ShipToState?.trim() === customer?.ShipToState?.trim() &&
                row.ShipToCity === customer?.ShipToCity &&
                row.ShipToName === customer?.ShipToName
            )
        })
        if (defaultShipToIndex > -1) {
            let defaultShipTo = { ...res.splice(defaultShipToIndex, 1)[0] }
            defaultShipTo.isDefault = true
            res.sort((a, b) => (a.ShipToName! > b.ShipToName! ? 1 : -1))
            return [defaultShipTo, ...res]
        }
        return res
    }
)
export default customerSlice.reducer
